'use client';

import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'next/navigation';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { Trash2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { PageHeader } from '@/components/PageHeader';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { ProductInfoFormComponent } from '@/components/common/ProductInfoFormComponent';
import { Command } from '@/types/production/command';
import { getCommandList } from '@/services/production/commandService';
import { Lot } from '@/types/production/lot';
import { useMbomListByRootItem, MbomItem } from '@/hooks/production/working/useMbomListByRootItem';
import { useWorkingBufferListQuery } from '@/hooks/production/working/useWorkingBuffer';
import { useQuery } from '@tanstack/react-query';
import { useCreateWorkingBufferList } from '@/hooks/production/working/useCreateWorkingBuffer';
import { CreateWorkingBufferPayload } from '@/types/production/workingBuffer';
import { WorkingBuffer } from '@/types/production/workingBuffer';
import { getUserList } from '@/services/users/userService';
import { Spinner } from '@/components/ui/spinner';

interface InputtedMaterial {
  id: number;
  itemId: number;
  barcode: string;
  number: string;
  name: string;
  spec: string;
  qty: number;
  weight: number | undefined;
  inputNum: number | undefined;
  inputDate: string;
  inputBy: string;
}

interface ProcessGroup {
  progressId: number;
  progressName: string;
  progressTypeCode: string;
  itemId: number;
  itemNumber: string;
  itemName: string;
  itemSpec: string;
  availableLotCount: number;
  inputtedCount: number;
  lots: Lot[];
}

export default function MultiMaterialInput() {
  const { t } = useTranslation();
  const searchParams = useSearchParams();
  const [workDate, setWorkDate] = useState<Date | undefined>(undefined);
  const [commandNo, setCommandNo] = useState<string>('');
  const [selectedCommand, setSelectedCommand] = useState<Command | null>(null);
  const [commandId, setCommandId] = useState<number | undefined>(undefined);
  const [machineName, setMachineName] = useState<string>('');
  const [itemNo, setItemNo] = useState<string>('');
  const [itemNumber, setItemNumber] = useState<string>('');
  const [itemName, setItemName] = useState<string>('');
  const [itemSpec, setItemSpec] = useState<string>('');
  const [commandAmount, setCommandAmount] = useState<string>('');
  const [progressId, setProgressId] = useState<number | undefined>(undefined);
  const [progressName, setProgressName] = useState<string>('');
  const [preProgressId, setPreProgressId] = useState<number | undefined>(undefined);
  const [progressOrder, setProgressOrder] = useState<number | undefined>(undefined);
  const [commandGroupNo, setCommandGroupNo] = useState<string>('');
  const [previousProgressCommand, setPreviousProgressCommand] = useState<Command | null>(null);
  const [inputtedItems, setInputtedItems] = useState<InputtedMaterial[]>([]);
  const [scanValue, setScanValue] = useState('');
  const [workerId, setWorkerId] = useState<string>('');
  const [workerOptions, setWorkerOptions] = useState<{ id: number; name: string }[]>([]);
  const [isWorkerLoading, setIsWorkerLoading] = useState<boolean>(false);
  const [isSearching, setIsSearching] = useState<boolean>(false);

  // Two-level view: Processes → LOTs
  const [availableProcesses, setAvailableProcesses] = useState<ProcessGroup[]>([]); // Level 1: Processes shown in "투입 가능한 공정 리스트"
  const [selectedProcess, setSelectedProcess] = useState<ProcessGroup | null>(null); // Selected process
  const [availableLots, setAvailableLots] = useState<Lot[]>([]); // Level 2: LOTs for selected process shown in "등록된 자재"
  const [isLoadingProcesses, setIsLoadingProcesses] = useState(false);
  const [inputtedLotNos, setInputtedLotNos] = useState<Set<string>>(new Set());

  // Track which LOTs are selected in "등록된 자재" for saving (multiple select with checkbox)
  const [selectedLotNos, setSelectedLotNos] = useState<Set<string>>(new Set());

  // Fetch MBOM to get expected processes for this command
  const mbomSearchRequest = useMemo(() => {
    if (!progressId) return undefined;
    return { parentProgressId: progressId };
  }, [progressId]);

  const { data: mbomData, isLoading: isMbomLoading } = useMbomListByRootItem(
    selectedCommand?.itemId,
    1,
    !!(selectedCommand?.itemId && progressId),
    mbomSearchRequest
  );

  // Initialize work date
  useEffect(() => {
    setWorkDate(new Date());
  }, []);

  // 작업지시번호 쿼리(commandNo)가 있으면 자동 입력 + 검색
  useEffect(() => {
    const initialCommandNo = searchParams.get('commandNo');
    if (initialCommandNo && initialCommandNo.trim()) {
      setCommandNo(initialCommandNo);
      // 쿼리로 전달된 값을 바로 사용해 검색 실행
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      handleCommandNoSearch(initialCommandNo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Fetch worker list
  useEffect(() => {
    const fetchWorkers = async () => {
      setIsWorkerLoading(true);
      try {
        const response = await getUserList({}, 0, 500);
        let users: any[] = [];
        if (Array.isArray(response)) {
          users = response;
        } else if (response?.content && Array.isArray(response.content)) {
          users = response.content;
        }

        const options =
          Array.isArray(users) && users.length > 0
            ? users.map((u: any) => ({
                id: u.id,
                name: u.name || u.username || `User ${u.id}`,
              }))
            : [];
        setWorkerOptions(options);
      } catch (error) {
        console.error('작업자 목록 조회 실패:', error);
        toast.error(t('materialInput.workerListError'));
      } finally {
        setIsWorkerLoading(false);
      }
    };

    fetchWorkers();
  }, []);

  // PC version: Initial load - fetch working-buffer without filters
  const { data: initialWorkingBufferData } = useWorkingBufferListQuery({
    searchRequest: undefined, // No filters on initial load
    page: 0,
    size: 1000,
  });

  // PC version: Initial load - fetch command without filters
  const { data: initialCommandData } = useQuery({
    queryKey: ['command', 'initial'],
    queryFn: async () => {
      const response = await getCommandList({}, 0, 100);
      return response?.data?.content || response?.content || [];
    },
    enabled: true,
    staleTime: 1000 * 60 * 5,
  });

  // Fetch available processes and LOTs - Two-level view
  // Step 1: Get expected processes from MBOM
  // Step 2: Get available LOTs from API
  // Step 3: Merge them to show all expected processes, even if no LOTs available
  useEffect(() => {
    const fetchAvailableProcesses = async () => {
      if (!selectedCommand?.itemId || !progressId) {
        setAvailableProcesses([]);
        setSelectedProcess(null);
        setAvailableLots([]);
        return;
      }

      setIsLoadingProcesses(true);
      try {
        // Step 1: Build expected process list from MBOM
        const expectedProcessMap = new Map<number, ProcessGroup>();

        if (mbomData && Array.isArray(mbomData)) {
          mbomData
            .filter((item) => !item.isRoot && item.isRoot !== true)
            .forEach((mbomItem: any) => {
              const itemProgressId =
                mbomItem.itemProgress?.id || mbomItem.itemProgressId || mbomItem.item_progress_id;
              const itemId = mbomItem.item?.id || mbomItem.itemId || mbomItem.item_id;
              const itemNumber = mbomItem.item?.itemNumber || mbomItem.itemNumber;
              const itemName = mbomItem.item?.itemName || mbomItem.itemName;
              const itemSpec = mbomItem.item?.itemSpec || mbomItem.itemSpec;
              const progressName =
                mbomItem.itemProgress?.progressName || mbomItem.progressName || 'Unknown Process';
              const progressTypeCode =
                mbomItem.itemProgress?.progressTypeCode || mbomItem.progressTypeCode || '';

              if (itemProgressId && !expectedProcessMap.has(itemProgressId)) {
                expectedProcessMap.set(itemProgressId, {
                  progressId: itemProgressId,
                  progressName: progressName,
                  progressTypeCode: progressTypeCode,
                  itemId: itemId || 0,
                  itemNumber: itemNumber || '',
                  itemName: itemName || '',
                  itemSpec: itemSpec || '',
                  availableLotCount: 0,
                  inputtedCount: 0,
                  lots: [],
                });
              }
            });
        }

        // Step 2: Fetch available LOTs
        const { getInputableLotList } = await import('@/services/production/lotService');

        if (preProgressId) {
          // 2번째 공정 이후: 이전 공정에서 완료된 LOT를 가져옴
          const lotResult = await getInputableLotList(
            selectedCommand.itemId,
            preProgressId,
            commandId || null,
            500
          );

          // Extract LOTs
          let lots: any[] = [];
          if (Array.isArray(lotResult)) {
            lots = lotResult;
          } else if (lotResult?.content && Array.isArray(lotResult.content)) {
            lots = lotResult.content;
          } else if (lotResult?.data && Array.isArray(lotResult.data)) {
            lots = lotResult.data;
          }

          // Transform to Lot format
          const transformedLots: Lot[] = lots.map((lot: any) => ({
            lotMasterId: lot.id || lot.lotMasterId,
            lotNo: lot.lotNo,
            itemId: lot.itemId,
            itemNo: lot.itemNo,
            itemNumber: lot.itemNumber,
            itemName: lot.itemName,
            itemSpec: lot.itemSpec,
            lotAmount: lot.lotAmount,
            restAmount: lot.restAmount,
            lotUnit: lot.lotUnit,
            progressId: lot.progressId,
            progressName: lot.progressName,
            progressTypeCode: lot.progressTypeCode,
            commandId: lot.commandId,
            isDelete: false,
            tenantId: lot.tenantId || 0,
          }));

          // Step 3: Merge MBOM expected processes with available LOTs
          transformedLots.forEach((lot) => {
            const progressId = lot.progressId || 0;

            // If this process exists in MBOM, add LOTs to it
            if (expectedProcessMap.has(progressId)) {
              const processGroup = expectedProcessMap.get(progressId)!;
              processGroup.lots.push(lot);
            } else {
              // If process not in MBOM but has LOTs, still create it
              if (!expectedProcessMap.has(progressId)) {
                expectedProcessMap.set(progressId, {
                  progressId: progressId,
                  progressName: lot.progressName || 'Unknown Process',
                  progressTypeCode: lot.progressTypeCode || '',
                  itemId: lot.itemId || 0,
                  itemNumber: lot.itemNumber || '',
                  itemName: lot.itemName || '',
                  itemSpec: lot.itemSpec || '',
                  availableLotCount: 0,
                  inputtedCount: 0,
                  lots: [],
                });
              }
              expectedProcessMap.get(progressId)!.lots.push(lot);
            }
          });
        } else {
          // 첫 번째 공정: MBOM에서 가져온 각 공정의 자재 정보를 기반으로 LOT 조회
          const processGroups = Array.from(expectedProcessMap.values());

          // 각 공정별로 해당 자재의 LOT를 조회
          for (const processGroup of processGroups) {
            if (processGroup.itemId && processGroup.progressId) {
              try {
                const lotResult = await getInputableLotList(
                  processGroup.itemId,
                  processGroup.progressId,
                  commandId || null,
                  500
                );

                // Extract LOTs
                let lots: any[] = [];
                if (Array.isArray(lotResult)) {
                  lots = lotResult;
                } else if (lotResult?.content && Array.isArray(lotResult.content)) {
                  lots = lotResult.content;
                } else if (lotResult?.data && Array.isArray(lotResult.data)) {
                  lots = lotResult.data;
                }

                // Transform to Lot format
                const transformedLots: Lot[] = lots.map((lot: any) => ({
                  lotMasterId: lot.id || lot.lotMasterId,
                  lotNo: lot.lotNo,
                  itemId: lot.itemId,
                  itemNo: lot.itemNo,
                  itemNumber: lot.itemNumber,
                  itemName: lot.itemName,
                  itemSpec: lot.itemSpec,
                  lotAmount: lot.lotAmount,
                  restAmount: lot.restAmount,
                  lotUnit: lot.lotUnit,
                  progressId: lot.progressId,
                  progressName: lot.progressName,
                  progressTypeCode: lot.progressTypeCode,
                  commandId: lot.commandId,
                  isDelete: false,
                  tenantId: lot.tenantId || 0,
                }));

                // 해당 공정에 LOT 추가
                processGroup.lots.push(...transformedLots);
              } catch (error) {
                console.error(`Error fetching lots for process ${processGroup.progressId}:`, error);
                // 에러가 발생해도 다른 공정의 LOT는 계속 조회
              }
            }
          }
        }

        // Step 4: Calculate counts for each process
        const processGroups = Array.from(expectedProcessMap.values());

        // 4-1. inputtedItems 기준으로 itemId별 투입 건수 집계
        const itemInputCountMap = new Map<number, number>();
        for (const item of inputtedItems) {
          if (!item.itemId) continue;
          itemInputCountMap.set(item.itemId, (itemInputCountMap.get(item.itemId) || 0) + 1);
        }

        // 4-2. 공정별로 availableLotCount, inputtedCount 세팅
        processGroups.forEach((processGroup) => {
          processGroup.availableLotCount = processGroup.lots.length;
          processGroup.inputtedCount = itemInputCountMap.get(processGroup.itemId) || 0;
        });

        setAvailableProcesses(processGroups);

        // Auto-select first process if available
        if (processGroups.length > 0) {
          setSelectedProcess(processGroups[0]);
          setAvailableLots(processGroups[0].lots);
        } else {
          setSelectedProcess(null);
          setAvailableLots([]);
        }
      } catch (error) {
        console.error('❌ Error fetching processes:', error);
        toast.error(t('materialInput.processInfoError'));
        setAvailableProcesses([]);
        setSelectedProcess(null);
        setAvailableLots([]);
      } finally {
        setIsLoadingProcesses(false);
      }
    };

    fetchAvailableProcesses();
  }, [mbomData, selectedCommand?.itemId, progressId, preProgressId, commandId, inputtedLotNos]);

  // Fetch working-buffer by targetCommandId to show "투입된 자재 리스트" (Already inputted materials)
  const { data: workingBufferData, isLoading: isWorkingBufferLoading } = useWorkingBufferListQuery({
    searchRequest:
      commandId && progressId
        ? {
            targetCommandId: commandId, // KEY: Search by targetCommandId (where materials were inputted TO)
            targetProgressId: progressId, // KEY: Also need targetProgressId for the query to be enabled
          }
        : undefined,
    page: 0,
    size: 1000,
  });

  // Extract content from different possible response structures
  // "투입된 자재 리스트"에는 bufferType이 INPUT인 것만 표시
  const workingBufferContent = useMemo<WorkingBuffer[]>(() => {
    if (!workingBufferData) return [];
    let list: WorkingBuffer[] = [];
    if (Array.isArray(workingBufferData)) {
      list = workingBufferData;
    } else if (workingBufferData?.content && Array.isArray(workingBufferData.content)) {
      list = workingBufferData.content;
    } else if (workingBufferData?.data?.content && Array.isArray(workingBufferData.data.content)) {
      list = workingBufferData.data.content;
    }

    return list.filter((wb) => wb.bufferType === 'INPUT');
  }, [workingBufferData]);

  // Update inputted LOT numbers and display list when working buffer data changes
  useEffect(() => {
    const content = workingBufferContent;

    if (content.length > 0) {
      // Track which LOT numbers have already been inputted
      const inputtedNos = new Set<string>(
        content
          .map((wb: WorkingBuffer) => wb.lotNo)
          .filter((lotNo: string | undefined): lotNo is string => Boolean(lotNo))
      );
      setInputtedLotNos(inputtedNos);

      // Transform working buffer data for display in "투입된 자재 리스트"
      const inputted: InputtedMaterial[] = content.map((wb: WorkingBuffer, index: number) => {
        const wbId = (wb as any).id || wb.id || index;
        return {
          id: wbId,
          itemId: wb.itemId || 0,
          barcode: wb.lotNo || '',
          number: wb.itemNumber || '',
          name: wb.itemName || '',
          spec: wb.itemSpec || '',
          qty: wb.inputNum || 0,
          weight: wb.inputWeight,
          inputNum: wb.inputNum,
          inputDate: wb.inputDate || format(new Date(), 'yyyy-MM-dd'),
          inputBy: wb.inputBy || '',
        };
      });
      setInputtedItems(inputted);
    } else {
      setInputtedLotNos(new Set());
      setInputtedItems([]);
    }
  }, [workingBufferContent]);

  // Command search handler
  const handleCommandNoSearch = async (searchNo?: string) => {
    const raw = searchNo ?? commandNo;
    if (!raw || raw.trim() === '') {
      toast.error(t('materialInput.commandNoRequired'));
      return;
    }

    setIsSearching(true);
    try {
      // Extract commandGroupNo from commandNo (part before "-")
      // Example: "2512050001-02" -> commandGroupNo: "2512050001"
      // Normalize commandNo:
      // - trim spaces
      // - remove any characters that are not digits or '-'
      //   (e.g. accidental trailing '.' from barcode scanners or user input)
      const trimmedCommandNo = raw.trim().replace(/[^0-9-]/g, '');
      const commandGroupNo = trimmedCommandNo.includes('-')
        ? trimmedCommandNo.split('-')[0]
        : trimmedCommandNo;

      // PC version: First search by exact commandNo
      const response = await getCommandList({ commandNo: trimmedCommandNo }, 0, 100);
      const content = response?.data?.content || response?.content || [];

      let command: Command | null = null;
      if (Array.isArray(content) && content.length > 0) {
        // Find exact match by commandNo
        command = content.find((c: Command) => c.commandNo === trimmedCommandNo) || content[0];
      }

      if (!command) {
        toast.error(t('materialInput.commandNotFound'));
        setSelectedCommand(null);
        setCommandId(undefined);
        setProgressId(undefined);
        return;
      }

      setSelectedCommand(command);
      setCommandId(command.id); // This is targetCommandId for working buffer
      setProgressId(command.progressId); // This is targetProgressId for working buffer

      // Store additional fields
      const cmd = command as any;
      setPreProgressId(cmd.preProgressId);
      setProgressOrder(cmd.progressOrder || 1);
      setCommandGroupNo(cmd.commandGroupNo || '');

      // Update form fields
      setItemNo(command.itemNo?.toString() || '');
      setItemNumber(command.itemNumber || '');
      setItemName(command.itemName || '');
      setItemSpec(command.itemSpec || '');
      setProgressName(command.progressName || '');

      // Clear previous selections - LOTs will be fetched automatically
      setSelectedLotNos(new Set());

      if (command.commandAmount) {
        const amount = command.commandAmount.toString();
        const unit = command.unit || 'EA';
        setCommandAmount(`${amount.toLocaleString()} ${unit}`);
      }

      if (command.machineName) {
        setMachineName(command.machineName);
      }

      toast.success(t('materialInput.commandLoaded'));
    } catch (error) {
      console.error('작업지시 조회 실패:', error);
      toast.error(t('materialInput.commandSearchError'));
      setSelectedCommand(null);
      setCommandId(undefined);
      setProgressId(undefined);
    } finally {
      setIsSearching(false);
    }
  };

  // Columns for "투입 가능한 공정 리스트" (Available Processes)
  const processColumns = useMemo(
    () => [
      {
        key: 'index',
        header: t('materialInput.columns.index'),
        width: 'w-16',
        align: 'text-center',
        render: (process: ProcessGroup, index: number) => index + 1,
      },
      {
        key: 'progressName',
        header: t('materialInput.columns.progressName'),
        width: 'w-32',
        align: '',
        render: (process: ProcessGroup) => process.progressName,
      },
      {
        key: 'itemNumber',
        header: t('materialInput.columns.itemNumber'),
        width: 'w-28',
        align: '',
        render: (process: ProcessGroup) => process.itemNumber,
      },
      {
        key: 'itemName',
        header: t('materialInput.columns.itemName'),
        width: 'w-32',
        align: '',
        render: (process: ProcessGroup) => process.itemName,
      },
      {
        key: 'itemSpec',
        header: t('materialInput.columns.itemSpec'),
        width: 'w-25',
        align: '',
        render: (process: ProcessGroup) => process.itemSpec,
      },
      {
        key: 'availableLotCount',
        header: t('materialInput.columns.availableLotCount'),
        width: 'w-24',
        align: 'text-center',
        render: (process: ProcessGroup) =>
          `${process.availableLotCount}${t('materialInput.cases')}`,
      },
      {
        key: 'inputtedCount',
        header: t('materialInput.columns.inputtedCount'),
        width: 'w-24',
        align: 'text-center',
        render: (process: ProcessGroup) => `${process.inputtedCount}${t('materialInput.cases')}`,
      },
    ],
    [t]
  );

  // Columns for "등록된 자재" (Available LOTs for selected process) - checkbox selection
  const availableLotColumns = useMemo(
    () => [
      {
        key: 'index',
        header: t('materialInput.columns.index'),
        width: 'w-16',
        align: 'text-center',
        render: (lot: Lot, index: number) => index + 1,
      },
      {
        key: 'lotNo',
        header: t('materialInput.columns.lotNo'),
        width: 'w-32',
        align: '',
        render: (lot: Lot) => lot.lotNo || '-',
      },
      {
        key: 'itemNumber',
        header: t('materialInput.columns.itemNumber'),
        width: 'w-28',
        align: '',
        render: (lot: Lot) => lot.itemNumber || '-',
      },
      {
        key: 'itemName',
        header: t('materialInput.columns.itemName'),
        width: 'w-32',
        align: '',
        render: (lot: Lot) => lot.itemName || '-',
      },
      {
        key: 'itemSpec',
        header: t('materialInput.columns.itemSpec'),
        width: 'w-25',
        align: '',
        render: (lot: Lot) => lot.itemSpec || '-',
      },
      {
        key: 'lotAmount',
        header: t('materialInput.columns.quantity'),
        width: 'w-20',
        align: 'text-right',
        render: (lot: Lot) => (lot.lotAmount || 0).toLocaleString(),
      },
      {
        key: 'restAmount',
        header: t('materialInput.columns.restQuantity'),
        width: 'w-20',
        align: 'text-right',
        render: (lot: Lot) => (lot.restAmount || 0).toLocaleString(),
      },
      {
        key: 'lotUnit',
        header: t('materialInput.columns.unit'),
        width: 'w-20',
        align: 'text-center',
        render: (lot: Lot) => lot.lotUnit || '-',
      },
    ],
    [t]
  );

  // 투입된 자재 테이블 컬럼 정의
  const inputtedMaterialColumns = useMemo(
    () => [
      {
        key: 'barcode',
        header: t('materialInput.columns.inputLOT'),
        align: '',
        render: (item: InputtedMaterial) => item.barcode,
      },
      {
        key: 'number',
        header: t('materialInput.columns.itemNumber'),
        align: '',
        render: (item: InputtedMaterial) => item.number,
      },
      {
        key: 'name',
        header: t('materialInput.columns.itemName'),
        align: '',
        render: (item: InputtedMaterial) => item.name,
      },
      {
        key: 'spec',
        header: t('materialInput.columns.itemSpec'),
        align: '',
        render: (item: InputtedMaterial) => item.spec,
      },
      {
        key: 'weight',
        header: t('materialInput.columns.weight') || '중량',
        align: 'text-right',
        render: (item: InputtedMaterial) => (item.weight ? item.weight.toLocaleString() : '-'),
      },
      {
        key: 'inputNum',
        header: t('materialInput.columns.inputAmount') || '투입수량',
        align: 'text-center',
        render: (item: InputtedMaterial) => (item.inputNum ? item.inputNum.toLocaleString() : '-'),
      },
      {
        key: 'inputDate',
        header: t('materialInput.columns.inputDate'),
        align: '',
        render: (item: InputtedMaterial) => item.inputDate,
      },
      {
        key: 'inputBy',
        header: t('materialInput.columns.inputBy'),
        align: '',
        render: (item: InputtedMaterial) => item.inputBy,
      },
    ],
    [t]
  );

  // Handle clicking a process to view its LOTs
  const handleProcessClick = (process: ProcessGroup) => {
    setSelectedProcess(process);
    setAvailableLots(process.lots);
    setSelectedLotNos(new Set()); // Clear selection when switching process
  };

  // Handle clicking a LOT in "등록된 자재" - Toggle checkbox selection
  const handleLotClick = (lot: Lot) => {
    if (!selectedProcess) {
      toast.error(t('materialInput.selectProcessFirst'));
      return;
    }

    // Check if already inputted
    if (lot.lotNo && inputtedLotNos.has(lot.lotNo)) {
      toast.warning(t('materialInput.materialAlreadyInputted'));
      return;
    }

    // Toggle selection
    if (lot.lotNo) {
      setSelectedLotNos((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(lot.lotNo!)) {
          newSet.delete(lot.lotNo!);
        } else {
          newSet.add(lot.lotNo!);
        }
        return newSet;
      });
    }
  };

  // LOT 입력 처리 (scan by LOT number) - Toggle selection
  const handleScan = (code: string) => {
    if (!code.trim()) return;

    if (!selectedProcess) {
      toast.error(t('materialInput.selectProcessFirst'));
      return;
    }

    // Find LOT from available lots
    const foundLot = availableLots.find((m) => m.lotNo === code);

    if (!foundLot) {
      toast.error(t('materialInput.materialNotRegistered'));
      setScanValue('');
      return;
    }

    // Check if already inputted
    if (inputtedLotNos.has(code)) {
      toast.error(t('materialInput.materialAlreadyInputted'));
      setScanValue('');
      return;
    }

    // Check if already selected - don't toggle, just show message
    if (foundLot.lotNo && selectedLotNos.has(foundLot.lotNo)) {
      toast.info(t('materialInput.materialAlreadySelected') || '이미 선택된 자재입니다.');
      setScanValue('');
      return;
    }

    // Add to selection (only if not already selected)
    if (foundLot.lotNo) {
      setSelectedLotNos((prev) => {
        const newSet = new Set(prev);
        newSet.add(foundLot.lotNo!);
        return newSet;
      });

      toast.success(t('materialInput.materialSelected'));
    }
    setScanValue('');
  };

  // Create WorkingBuffer mutation
  const createWorkingBufferList = useCreateWorkingBufferList();

  // 저장 - Save selected LOTs (multiple)
  const handleSave = async () => {
    if (!selectedProcess) {
      toast.error(t('materialInput.selectProcessFirst'));
      return;
    }

    // Check if LOTs are selected
    if (selectedLotNos.size === 0) {
      toast.error(t('materialInput.selectMaterialsToSave'));
      return;
    }

    if (!commandId || !progressId) {
      toast.error(t('materialInput.searchWorkOrderFirst'));
      return;
    }

    // Use first worker as default if not selected
    const selectedWorkerId =
      workerId || (workerOptions.length > 0 ? workerOptions[0].id.toString() : '');
    if (!selectedWorkerId) {
      toast.error(t('materialInput.workerInfoError'));
      return;
    }

    // Find all selected LOTs from available lots
    const selectedLots = availableLots.filter((lot) => lot.lotNo && selectedLotNos.has(lot.lotNo));
    if (selectedLots.length === 0) {
      toast.error(t('materialInput.selectedMaterialsNotFound'));
      return;
    }

    // Create WorkingBuffer payloads for all selected LOTs
    const payloads: CreateWorkingBufferPayload[] = selectedLots.map((selectedLot) => {
      // Validate required fields
      if (!selectedLot.lotMasterId) {
        throw new Error(`${t('materialInput.errors.lotMasterIdMissing')}: ${selectedLot.lotNo}`);
      }
      if (!selectedLot.lotNo) {
        throw new Error(`${t('materialInput.errors.lotNoMissing')}: ${selectedLot.itemName}`);
      }
      if (!selectedLot.itemId) {
        throw new Error(`${t('materialInput.errors.itemIdMissing')}: ${selectedLot.itemName}`);
      }

      const inputNum = selectedLot.restAmount || 0;
      const inputWeight: number | undefined = selectedLot.restWeight ?? undefined;

      // Get worker name
      const workerName =
        workerOptions.find((w) => w.id.toString() === selectedWorkerId)?.name || '';
      const currentDate = format(new Date(), 'yyyy-MM-dd');

      // Build payload matching database structure:
      // Source fields: lotMasterId, lotNo, itemId, progressId (where material comes FROM)
      // Target fields: targetCommandId, targetItemId, targetProgressId (where material goes TO)
      const payload: CreateWorkingBufferPayload = {
        // SOURCE: LOT info (from lot_master table)
        lotMasterId: selectedLot.lotMasterId,
        lotNo: selectedLot.lotNo || '',
        itemId: selectedLot.itemId,
        itemNo: selectedLot.itemNo || undefined,
        itemNumber: selectedLot.itemNumber || '',
        itemName: selectedLot.itemName || '',
        itemSpec: selectedLot.itemSpec || undefined,
        progressId: selectedLot.progressId || undefined,
        progressName: selectedLot.progressName || undefined,
        progressTypeCode: selectedLot.progressTypeCode || undefined,

        // Input quantities
        inputNum: inputNum,
        inputWeight: inputWeight,
        unit: selectedLot.lotUnit || 'EA',
        inputDate: currentDate,
        inputBy: workerName,
        isExhaust: 'N',
        bufferType: 'INPUT',

        // TARGET: Command info (from commands table) - where material is being inputted TO
        targetCommandId: commandId, // KEY: commands.command_id
        targetCommandNo: commandNo || undefined,
        targetItemId: selectedCommand?.itemId, // commands.item_id
        targetItemNo: selectedCommand?.itemNo,
        targetItemNumber: selectedCommand?.itemNumber,
        targetItemName: selectedCommand?.itemName,
        targetItemSpec: selectedCommand?.itemSpec,
        targetProgressId: progressId, // commands.progress_id
        targetProgressName: progressName || undefined,
        targetProgressTypeCode: (selectedCommand as any)?.progressTypeCode || undefined,
      };

      // Remove undefined/null/empty values
      const cleanedPayload: any = {};
      Object.keys(payload).forEach((key) => {
        const value = payload[key as keyof CreateWorkingBufferPayload];
        if (value !== undefined && value !== null && value !== '') {
          cleanedPayload[key] = value;
        }
      });

      return cleanedPayload as CreateWorkingBufferPayload;
    });

    try {
      await createWorkingBufferList.mutateAsync(payloads);
      // Clear the selections
      setSelectedLotNos(new Set());
      // The working buffer query will be invalidated by the mutation hook,
      // which will automatically refresh 투입된 자재 리스트
      toast.success(`${selectedLots.length}${t('materialInput.materialsSaved')}`);
    } catch (error) {
      console.error('자재 투입 실패:', error);
      // Error toast is handled in the mutation
    }
  };

  return (
    <div className="flex flex-col bg-background">
      <PageHeader title={t('materialInput.title')} />

      <main className="flex-1 p-3 sm:p-4 md:p-6">
        <div className="container mx-auto space-y-4 md:space-y-6">
          {/* 작업지시 정보 */}
          <ProductInfoFormComponent
            commandNo={commandNo}
            command={selectedCommand}
            onCommandNoChange={setCommandNo}
            onSearch={handleCommandNoSearch}
          />

          {/* 투입 가능한 자재 리스트 and 등록된 자재 - Side by side in one row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
            {/* 왼쪽: 입고LOT 입력 및 투입 가능한 자재 리스트 */}
            <div className="space-y-4 md:space-y-6 flex flex-col">
              {/* 입고LOT 입력창 */}
              <Card className="p-4 md:p-6">
                <div className="flex items-center gap-3">
                  <Label htmlFor="barcode-input" className="text-sm font-medium whitespace-nowrap">
                    {t('materialInput.barcodeInput')}
                  </Label>
                  <Input
                    id="barcode-input"
                    value={scanValue}
                    onChange={(e) => setScanValue(e.target.value.toUpperCase())}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleScan(scanValue);
                      }
                    }}
                    autoFocus
                    className="text-base flex-1"
                  />
                </div>
              </Card>

              {/* LEVEL 1: 투입 가능한 공정 리스트 - Shows available Processes */}
              <Card className="p-4 md:p-6 flex-1 flex flex-col">
                <div className="mb-4 pb-2 border-b">
                  <h3 className="text-sm md:text-base font-semibold">
                    {t('materialInput.availableProcesses')} ({availableProcesses.length}
                    {t('materialInput.cases')})
                  </h3>
                  {selectedCommand && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {t('materialInput.info.workOrder')}: {selectedCommand.commandNo} |{' '}
                      {t('materialInput.info.item')}: {selectedCommand.itemName} |{' '}
                      {t('materialInput.info.process')}: {selectedCommand.progressName}
                    </p>
                  )}
                  {!isLoadingProcesses &&
                    availableProcesses.length === 0 &&
                    selectedCommand &&
                    !preProgressId && (
                      <p className="text-xs text-amber-600 mt-1">
                        {t('materialInput.firstProcess')}
                      </p>
                    )}
                </div>
                <div className="rounded-md border overflow-x-auto max-h-[300px] overflow-y-auto">
                  {isLoadingProcesses || isMbomLoading ? (
                    <div className="p-8 text-center text-muted-foreground">
                      <Spinner className="h-6 w-6 mx-auto mb-2" />
                      <p className="text-sm md:text-base">{t('materialInput.loadingProcesses')}</p>
                    </div>
                  ) : availableProcesses.length > 0 ? (
                    <Table>
                      <TableHeader className="sticky top-0 z-10 bg-background">
                        <TableRow>
                          {processColumns.map((col) => (
                            <TableHead
                              key={col.key}
                              className={cn('bg-muted/50 whitespace-nowrap', col.width, col.align)}
                            >
                              {col.header}
                            </TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {availableProcesses.map((process: ProcessGroup, index: number) => {
                          const isSelected = selectedProcess?.progressId === process.progressId;

                          return (
                            <TableRow
                              key={`process-${process.progressId}-${index}`}
                              onClick={() => handleProcessClick(process)}
                              className={cn(
                                'cursor-pointer hover:bg-muted/30',
                                isSelected && 'bg-blue-50 dark:bg-blue-900/20'
                              )}
                            >
                              {processColumns.map((col) => (
                                <TableCell
                                  key={col.key}
                                  className={cn('whitespace-nowrap', col.align)}
                                >
                                  {col.render(process, index)}
                                </TableCell>
                              ))}
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  ) : (
                    <div className="p-8 text-center text-muted-foreground">
                      <p className="text-sm md:text-base">
                        {commandId
                          ? t('materialInput.noAvailableProcesses')
                          : t('materialInput.searchWorkOrder')}
                      </p>
                    </div>
                  )}
                </div>
              </Card>
            </div>

            {/* 오른쪽: 등록된 자재 - Shows available LOTs from selected process */}
            <div className="space-y-4 md:space-y-6 flex flex-col">
              {/* LEVEL 2: 등록된 자재 리스트 - Available LOTs */}
              <Card className="p-4 md:p-6 flex-1 flex flex-col">
                <div className="mb-4 pb-2 flex justify-between items-center border-b">
                  <div>
                    <h3 className="text-sm md:text-base font-semibold">
                      {t('materialInput.registeredMaterials')} ({availableLots.length}
                      {t('materialInput.cases')}){' '}
                      {selectedLotNos.size > 0 &&
                        `| ${t('materialInput.selected')}: ${selectedLotNos.size}${t('materialInput.cases')}`}
                    </h3>
                    {selectedProcess ? (
                      <p className="text-xs text-muted-foreground mt-1">
                        {t('materialInput.info.processInfo')}: {selectedProcess.progressName} |{' '}
                        {t('materialInput.info.selectAndSave')}
                      </p>
                    ) : (
                      <p className="text-xs text-muted-foreground mt-1">
                        {t('materialInput.info.selectProcessFromList')}
                      </p>
                    )}
                  </div>
                  <Button
                    onClick={handleSave}
                    disabled={selectedLotNos.size === 0 || createWorkingBufferList.isPending}
                    className="bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {createWorkingBufferList.isPending ? (
                      <>
                        <Spinner className="h-4 w-4 mr-2" />
                        {t('materialInput.saving')}
                      </>
                    ) : (
                      `${t('materialInput.saveSelectedCount')} (${selectedLotNos.size})`
                    )}
                  </Button>
                </div>
                {!selectedProcess ? (
                  <div className="p-8 text-center text-muted-foreground flex-1 flex items-center justify-center">
                    <p className="text-sm md:text-base">{t('materialInput.selectProcess')}</p>
                  </div>
                ) : availableLots.length > 0 ? (
                  <div className="rounded-md border overflow-x-auto max-h-[500px] overflow-y-auto">
                    <Table>
                      <TableHeader className="sticky top-0 z-10 bg-background">
                        <TableRow>
                          <TableHead className="w-16 text-center bg-muted/50">
                            {t('materialInput.columns.select')}
                          </TableHead>
                          {availableLotColumns.map((col) => (
                            <TableHead
                              key={col.key}
                              className={cn('bg-muted/50 whitespace-nowrap', col.width, col.align)}
                            >
                              {col.header}
                            </TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {availableLots.map((lot: Lot, index: number) => {
                          const isAlreadyInputted = !!(lot.lotNo && inputtedLotNos.has(lot.lotNo));
                          const isSelected = !!(lot.lotNo && selectedLotNos.has(lot.lotNo));

                          return (
                            <TableRow
                              key={`lot-${lot.lotMasterId}-${index}`}
                              onClick={() => !isAlreadyInputted && handleLotClick(lot)}
                              className={cn(
                                'cursor-pointer hover:bg-muted/30',
                                isAlreadyInputted && 'opacity-50 cursor-not-allowed',
                                isSelected && 'bg-blue-50 dark:bg-blue-900/20'
                              )}
                            >
                              <TableCell className="text-center">
                                <Checkbox
                                  checked={isSelected}
                                  onCheckedChange={() => !isAlreadyInputted && handleLotClick(lot)}
                                  disabled={isAlreadyInputted}
                                  className="h-4 w-4 cursor-pointer"
                                />
                              </TableCell>
                              {availableLotColumns.map((col) => (
                                <TableCell
                                  key={col.key}
                                  className={cn('whitespace-nowrap', col.align)}
                                >
                                  {col.render(lot, index)}
                                </TableCell>
                              ))}
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="p-8 text-center text-muted-foreground flex-1 flex items-center justify-center">
                    <p className="text-sm md:text-base">
                      {t('materialInput.noMaterialsForProcess')}
                    </p>
                  </div>
                )}
              </Card>
            </div>
          </div>

          {/* 투입된 자재 리스트 - Footer */}
          <Card className="p-4 md:p-6">
            <div className="mb-4 pb-2 border-b">
              <h3 className="text-sm md:text-base font-semibold">
                {t('materialInput.inputtedMaterials')} ({inputtedItems.length}
                {t('materialInput.cases')})
              </h3>
            </div>
            <div className="rounded-md border overflow-x-auto max-h-[400px] overflow-y-auto">
              {inputtedItems.length > 0 ? (
                <Table>
                  <TableHeader className="sticky top-0 z-10 bg-background">
                    <TableRow>
                      {inputtedMaterialColumns.map((col) => (
                        <TableHead
                          key={col.key}
                          className={cn('bg-muted/50 whitespace-nowrap', col.align || '')}
                        >
                          {col.header}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {inputtedItems.map((item) => (
                      <TableRow key={item.id}>
                        {inputtedMaterialColumns.map((col) => (
                          <TableCell
                            key={col.key}
                            className={cn('whitespace-nowrap', col.align || '')}
                          >
                            {col.render(item)}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="p-8 text-center text-muted-foreground">
                  <p className="text-sm md:text-base">{t('materialInput.noInputtedMaterials')}</p>
                </div>
              )}
            </div>
          </Card>
        </div>
      </main>
    </div>
  );
}
